// Comparateur de profils sur une même offre.
//
// POURQUOI IL EXISTE. Le panneau de score répond à « celui-ci convient-il ? ».
// La question réelle de la consultance est autre : « lequel des trois est-ce
// que je présente ? ». Y répondre avec un panneau par profil oblige à retenir
// cinq chiffres de tête pendant qu'on clique ailleurs — c'est-à-dire à ne pas
// comparer du tout.
//
// UNE COLONNE PAR PROFIL, UNE LIGNE PAR CRITÈRE : le seul agencement où l'oeil
// compare verticalement sans relire les libellés. Et sur chaque ligne, LE
// MEILLEUR EST DÉSIGNÉ — quatre barres de longueurs voisines ne se départagent
// pas à l'oeil, et une comparaison qu'il faut mesurer n'en est pas une.
//
// Comme ScorePanel, il ne prononce aucun verdict : il ne dit jamais « prends
// celui-là », il montre qui gagne sur quoi. L'arbitrage entre les dimensions
// appartient à l'humain qui connaît le client.

import type { ReactNode } from 'react'
import type { CompatibilityResult } from '@ai-job-fit/engine/scoring'
import { ScoreRing } from './ScoreRing'
import { scoreTone } from './scoreTone'
import { FR_SCORE_LABELS, EN_SCORE_LABELS, NL_SCORE_LABELS, IT_SCORE_LABELS, ES_SCORE_LABELS, type ScoreLabels } from './ScorePanel'
import type { UiLang } from '@ai-job-fit/engine/langues'

export interface CompareSubject {
  id: number
  name: string
  /** Rôle / spécialité, sous le nom. */
  role?: string
  result: CompatibilityResult
}

export interface CompareLabels extends ScoreLabels {
  score: string
  potentialRow: string
  best: string
  unique: string
  uniqueHelp: string
  common: string
  remove: string
}

export const FR_COMPARE_LABELS: CompareLabels = {
  ...FR_SCORE_LABELS,
  score: 'Score global',
  potentialRow: 'Après adaptation',
  best: 'meilleur',
  unique: 'Lui seul apporte',
  uniqueHelp: 'Atouts qu’aucun des autres profils comparés ne couvre.',
  common: 'Atouts communs à tous',
  remove: 'Retirer de la comparaison',
}

/** Le même contrat en anglais, pour la même raison qu'`EN_SCORE_LABELS` :
 *  la traduction du comparateur vit avec le comparateur, jamais dans une app. */
export const EN_COMPARE_LABELS: CompareLabels = {
  ...EN_SCORE_LABELS,
  score: 'Overall score',
  potentialRow: 'After tailoring',
  best: 'best',
  unique: 'Only they bring',
  uniqueHelp: 'Strengths none of the other compared profiles covers.',
  common: 'Strengths everyone shares',
  remove: 'Remove from the comparison',
}

export const NL_COMPARE_LABELS: CompareLabels = {
  ...NL_SCORE_LABELS,
  score: 'Totale score',
  potentialRow: 'Na aanpassing',
  best: 'beste',
  unique: 'Alleen deze brengt',
  uniqueHelp: 'Troeven die geen enkel ander vergeleken profiel dekt.',
  common: 'Troeven die iedereen deelt',
  remove: 'Uit de vergelijking halen',
}

export const IT_COMPARE_LABELS: CompareLabels = {
  ...IT_SCORE_LABELS,
  score: 'Punteggio complessivo',
  potentialRow: 'Dopo l’adattamento',
  best: 'migliore',
  unique: 'Solo lui o lei porta',
  uniqueHelp: 'Punti di forza che nessuno degli altri profili confrontati copre.',
  common: 'Punti di forza comuni a tutti',
  remove: 'Togli dal confronto',
}

export const ES_COMPARE_LABELS: CompareLabels = {
  ...ES_SCORE_LABELS,
  score: 'Puntuación global',
  potentialRow: 'Tras la adaptación',
  best: 'mejor',
  unique: 'Solo aporta',
  uniqueHelp: 'Puntos fuertes que ninguno de los otros perfiles comparados cubre.',
  common: 'Puntos fuertes comunes a todos',
  remove: 'Quitar de la comparación',
}

export const COMPARE_LABELS: Record<UiLang, CompareLabels> = { fr: FR_COMPARE_LABELS, en: EN_COMPARE_LABELS, nl: NL_COMPARE_LABELS, it: IT_COMPARE_LABELS, es: ES_COMPARE_LABELS }

export interface ScoreCompareProps {
  subjects: CompareSubject[]
  labels?: CompareLabels
  /** Retirer une colonne. Absent = colonnes non retirables. */
  onRemove?: (id: number) => void
  /** Action posée au pied de chaque colonne (générer le dossier…). */
  renderAction?: (subject: CompareSubject) => ReactNode
}

/** Comparaison de mots-clés insensible à la casse et aux espaces de bord. */
const norm = (s: string) => s.trim().toLowerCase()

/**
 * Croise les atouts des profils comparés : ce qu'ils ont TOUS, et ce que
 * chacun est SEUL à avoir.
 *
 * Exporté et pur pour être testable : c'est le seul vrai calcul de cet écran,
 * et se tromper d'un cran (un « seul » qui est en fait partagé) ferait
 * présenter le mauvais consultant sans qu'aucun écran ne le signale. L'IA
 * renvoie des libellés libres — « Power BI », « power bi » — d'où la
 * normalisation avant tout croisement, mais l'affichage garde la casse
 * d'origine du profil concerné.
 */
export function croiserAtouts(listes: string[][]): { communs: string[]; uniques: string[][] } {
  const sets = listes.map((l) => new Set(l.map(norm)))
  return {
    communs: (listes[0] ?? []).filter((k) => sets.every((set) => set.has(norm(k)))),
    uniques: listes.map((l, i) => l.filter((k) => sets.every((set, j) => j === i || !set.has(norm(k))))),
  }
}

export function ScoreCompare({
  subjects,
  labels = FR_COMPARE_LABELS,
  onRemove,
  renderAction,
}: ScoreCompareProps) {
  // Toutes les dimensions rencontrées, dans l'ordre du premier profil qui les
  // porte : l'IA peut en omettre une sur un profil et pas sur l'autre, et une
  // ligne manquante décalerait silencieusement la lecture.
  const dimKeys: string[] = []
  for (const s of subjects) {
    for (const d of s.result.dimensions ?? []) if (!dimKeys.includes(d.key)) dimKeys.push(d.key)
  }

  const dimScore = (s: CompareSubject, key: string) =>
    (s.result.dimensions ?? []).find((d) => d.key === key)?.score ?? null

  const { communs, uniques } = croiserAtouts(subjects.map((s) => s.result.matchedKeywords ?? []))

  const cols = `minmax(140px, 180px) repeat(${subjects.length}, minmax(180px, 1fr))`
  // Largeur plancher : au-delà, la grille défile DANS son conteneur. Calculée
  // ici et pas en CSS, parce qu'elle dépend du nombre de colonnes.
  const minWidth = 140 + subjects.length * 180

  /** Ligne de valeurs chiffrées : barre, chiffre, écart au meilleur. */
  const ligne = (titre: string, valeurs: (number | null)[], sep = true) => {
    const chiffres = valeurs.filter((v): v is number => v !== null)
    const max = chiffres.length ? Math.max(...chiffres) : null
    // Ne rien désigner quand tout le monde est à égalité : un « meilleur »
    // partout ne distingue rien et ajoute juste du bruit.
    const departage = max !== null && chiffres.some((v) => v !== max)
    return (
      <div className={`cmp-line ${sep ? 'cmp-row-sep' : ''}`} style={{ display: 'contents' }} key={titre}>
        <div className="cmp-rowlabel">{titre}</div>
        {valeurs.map((v, i) => (
          <div className="cmp-cell" key={subjects[i].id}>
            {v === null ? (
              <span className="cmp-none">—</span>
            ) : (
              <>
                <div className="cmp-val">
                  <span className="cmp-num">{v}</span>
                  {departage && v === max && <span className="cmp-best">{labels.best}</span>}
                  {departage && v !== max && <span className="cmp-delta">−{max! - v}</span>}
                </div>
                <div className="track">
                  <span className={`fill ${scoreTone(v)}`} style={{ width: `${v}%` }} />
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="cmp">
      <div className="cmp-scroll">
        <div className="cmp-grid" style={{ gridTemplateColumns: cols, minWidth }}>
          {/* --- En-têtes : l'anneau porte le score, comme partout ailleurs. */}
          <div className="cmp-rowlabel" />
          {subjects.map((s) => (
            <div className="cmp-cell cmp-headcell" key={s.id}>
              <div className="cmp-head">
                <ScoreRing
                  score={s.result.score}
                  potential={s.result.potentialScore}
                  tone={scoreTone(s.result.score)}
                  size={64}
                  ariaLabel={`${s.name} : ${s.result.score} sur 100`}
                />
                <div>
                  <div className="cmp-name">{s.name}</div>
                  {s.role && <div className="cmp-role">{s.role}</div>}
                </div>
                {onRemove && (
                  <button
                    className="cmp-drop"
                    onClick={() => onRemove(s.id)}
                    aria-label={`${labels.remove} : ${s.name}`}
                    title={labels.remove}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}

          {ligne(labels.score, subjects.map((s) => s.result.score))}
          {ligne(labels.potentialRow, subjects.map((s) => s.result.potentialScore ?? null))}
          {dimKeys.map((k) =>
            ligne(labels.dimensions[k] ?? k, subjects.map((s) => dimScore(s, k)))
          )}

          {/* --- Couperets : la seule ligne qui puisse disqualifier. */}
          <div className="cmp-row-sep cmp-band" style={{ display: 'contents' }}>
            <div className="cmp-rowlabel">{labels.dealBreakers}</div>
            {subjects.map((s) => {
              const db = s.result.dealBreakers ?? []
              return (
                <div className="cmp-cell" key={s.id}>
                  {db.length === 0 ? (
                    <span className="cmp-none">{labels.none}</span>
                  ) : (
                    <div className="cmp-tags">
                      {db.map((g, i) => (
                        <span className="cmp-tag block" key={i}>{g}</span>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* --- Ce que chacun est SEUL à apporter : la vraie différence entre
                  deux profils forts n'est pas dans les chiffres, elle est là. */}
          <div className="cmp-row-sep" style={{ display: 'contents' }}>
            <div className="cmp-rowlabel" title={labels.uniqueHelp}>{labels.unique}</div>
            {subjects.map((s, i) => (
              <div className="cmp-cell" key={s.id}>
                {uniques[i].length === 0 ? (
                  <span className="cmp-none">{labels.none}</span>
                ) : (
                  <div className="cmp-tags">
                    {uniques[i].map((k, j) => (
                      <span className="cmp-tag uniq" key={j}>{k}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {renderAction && (
            <div className="cmp-row-sep" style={{ display: 'contents' }}>
              <div className="cmp-rowlabel" />
              {subjects.map((s) => (
                <div className="cmp-cell" key={s.id}>{renderAction(s)}</div>
              ))}
            </div>
          )}
        </div>
      </div>

      {communs.length > 0 && (
        <div className="cmp-foot">
          <div className="gapbox plus">
            <div className="gh">{labels.common}</div>
            <ul>{communs.map((k, i) => <li key={i}>{k}</li>)}</ul>
          </div>
        </div>
      )}
    </div>
  )
}
